/*
  Eva Radio tape rack. Center slot is the focused/browsable tape, large
  and interactive; prev/next preview the adjacent tapes in the manifest
  array (smaller, faded, non-interactive), and render nothing at all at
  either end -- the rack is no-wrap with clean ends, so the outward slot
  by the first/last tape is simply empty (its width is still reserved,
  see assets/eva-radio.css, so the centre tape never shifts).
  Scroll/touch-drag/arrow buttons move which tape is focused.

  Per's final interaction spec (2026-07-23, supersedes every earlier
  version of this comment): each tape cover is a single image that
  cycles FRONT -> BACK -> SIDE A -> SIDE B -> FRONT, one step per click/
  tap (currentFace, purely cosmetic -- browsing/clicking never touches
  the deck). ONLY Side A/Side B are drag-into-deck targets; dragging
  while front/back is showing springs back and does nothing. Playback
  ownership, per Per ("the deck plays what's inside it, full stop"):
  focus (what the rack is showing/browsing) and the deck (what's
  actually inserted and playing) are two independent pieces of state --
  deckTapeId/deckSide vs. focusedTapeIndex/tape. The deck's contents
  change ONLY via:
    (a) an explicit drag onto the cassette door -- whichever side
        (currentFace) is showing when you insert decides which plays,
        replacing whatever was playing. The instant it lands, the rack
        image resets to the front cover (Per: "the moment playback
        starts, the rack image resets to the front cover") and stays
        there through pause/stop/natural side-flips -- nothing but a
        fresh insert or browsing away ever changes it again;
    (b) the natural end-of-side flip flow (eva:radio:tape:side, fired by
        eva-radio.js's flipToSide()) -- the existing bubble/accessible
        fallback, unchanged, but this only updates deckTapeId/deckSide
        and the deck-window overlay/hubs, never the rack's own face.
  STOP and PAUSE only ever affect audio + the deck-window overlay/hubs,
  never the rack card: no eject animation, no face change. Pause freezes
  the inside-deck overlay in place; stop fades it out (deckStopped,
  driven by eva:radio:playstate's reason field -- see applyDeckOverlay()).
  isFocusedInDeck() (deckTapeId === the currently-browsed tape's id) is
  the one derived check everything else reads: the drag tooltip/in-deck
  dim mark (updateDragTooltip).

  Manifest shape: assets/eva-tapes.json, schema v1, fetched once by
  eva-radio.js. A dev-only rack prototype (behind ?tapedev=1, never
  shipped) lives at the bottom.
*/
(function () {
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  var DRAG_THRESHOLD = 6;
  // Insert-flight duration (Phase 2b, 2026-08-05): tied to the real
  // on-screen rack-to-deck distance instead of one fixed value. That
  // distance genuinely varies a lot by viewport -- measured live via
  // Playwright: ~1580px on a 1440px desktop layout vs ~630-680px across
  // 375/390/430px mobile widths (the mobile rack sits directly below the
  // radio, desktop's sits to its right past the whole widget) -- so a
  // fixed duration meant the flight visibly moved over 2x faster (px/ms)
  // on desktop than on mobile. INSERT_SPEED_PX_PER_MS is calibrated so
  // the desktop distance still lands almost exactly on the old fixed
  // 420ms (1580 / 3.8 = ~416ms) -- that feel was already verified live,
  // this isn't meant to change it, only to make shorter/longer flights
  // scale proportionally instead of all sharing one duration. Clamped so
  // it never reads as instant (a real distance of 0 shouldn't happen,
  // but a tiny one could) or sluggish on some future much-larger layout.
  var INSERT_SPEED_PX_PER_MS = 3.8;
  var INSERT_DURATION_MIN_MS = 260;
  var INSERT_DURATION_MAX_MS = 520;
  function insertDurationForDistance(dist) {
    var raw = dist / INSERT_SPEED_PX_PER_MS;
    return Math.max(INSERT_DURATION_MIN_MS, Math.min(INSERT_DURATION_MAX_MS, Math.round(raw)));
  }
  var WHEEL_COOLDOWN_MS = 350; // one slot-shift per gesture, not one per wheel tick
  var SNAP_MARGIN_PX = 40; // magnetic zone around the deck face, see deckFaceRect()
  var DECK_FACE_SCALE_X = 2.2; // how much wider than the calibrated door hotspot "the whole deck face" drop target is
  var DECK_FACE_SCALE_Y = 1.8; // same, vertically -- the door polygon is already fairly tall, so this grows less than the horizontal scale

  // Insert "seat-in" clunk: a second, independent Audio instance playing
  // the SAME asset eva-radio.js's own mechStopAudio uses (window.
  // EvaRadioAssets.mechStop) -- no new sound asset needed, and a
  // dedicated instance here (rather than reaching into eva-radio.js's
  // module-private audio) matches the existing browseAudio pattern
  // below: the tape rack owns its own mechanical-sound playback rather
  // than coupling to the transport module. Fires the instant the tape
  // visually lands in the deck (end of insertFocusedTape()'s animation),
  // distinct from and slightly before the separate play-clack that
  // starts a few hundred ms later once playback actually begins.
  var CLUNK_GAIN = 0.07; // same RADIO_MECH_GAIN calibration eva-radio.js uses for this WAV set
  var clunkAudio = null;

  function playClunk() {
    if (!clunkAudio) { return; }
    try {
      clunkAudio.pause();
      clunkAudio.currentTime = 0;
      clunkAudio.volume = CLUNK_GAIN;
      var p = clunkAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  // Browse tick (rack scroll + insert): eva_ui_tape_browse.wav peaks at
  // -4.3dB raw (measured via ffmpeg volumedetect) -- close enough to the
  // other mech WAVs' own peaks (-1.4 to -3.8dB, same recording session)
  // that eva-radio.js's own RADIO_MECH_GAIN (0.07) applies here too,
  // rather than picking a new gain blind.
  var BROWSE_GAIN = 0.07;
  var browseAudio = null;

  function playBrowse() {
    if (!browseAudio) { return; }
    try {
      browseAudio.pause();
      browseAudio.currentTime = 0;
      browseAudio.volume = BROWSE_GAIN;
      var p = browseAudio.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  // --- Carousel-tick variant pool (2026-08-05) -----------------------------
  // A dedicated sound family for the RACK NAVIGATION tick specifically
  // (setFocusedTapeIndex(), i.e. wheel/touch/arrows/keyboard/tap-to-
  // centre) -- separate from playBrowse() above, which stays exactly as
  // it was and keeps covering the insert flight. window.EvaRadioAssets.
  // carouselTicks (theme.liquid) lists 6 short (90-120ms) mechanical
  // clicks cut from Per's clip43/clip44 references, each peak-normalized
  // to -3dBFS raw -- CAROUSEL_TICK_GAIN below brings that raw peak down
  // to land near eva-radio.js's RADIO_MECH_GAIN-calibrated siblings
  // (~-26dB effective peak, matching ui-hover.mp3's own raw peak per that
  // file's own calibration comment): 10^((-26-(-3))/20) ~= 0.071, i.e.
  // effectively the same 0.07 constant, not a coincidence -- these were
  // normalized to -3dB specifically so the existing calibration would
  // carry over without picking a second gain blind.
  var CAROUSEL_TICK_GAIN = 0.07;
  var CAROUSEL_TICK_POOL_SIZE = 4; // concurrent voices -- lets a fast run of steps overlap instead of cutting the previous tick off
  var CAROUSEL_TICK_PITCH_RANGE = 0.04; // +/-4%
  var CAROUSEL_RAPID_REPEAT_MS = 220; // a step faster than this counts toward the duck-on-hold streak
  var CAROUSEL_RAPID_DUCK_STEP = 0.12; // volume lost per consecutive rapid step
  var CAROUSEL_RAPID_DUCK_FLOOR = 0.45; // never duck below this fraction of base gain -- holding an arrow should stay audible, just less machine-gun

  var carouselTickUrls = [];
  var carouselTickVoices = [];
  var carouselTickVoiceCursor = 0;
  var carouselLastVariantIndex = -1;
  var carouselLastTickAt = 0;
  var carouselRapidStreak = 0;

  function initCarouselTicks(urls) {
    carouselTickUrls = urls || [];
    if (!carouselTickUrls.length) { return; }
    for (var i = 0; i < CAROUSEL_TICK_POOL_SIZE; i++) {
      var voice = new Audio();
      voice.preload = 'auto';
      if (window.EvaMute) { window.EvaMute.applyToElement(voice); }
      carouselTickVoices.push(voice);
    }
  }

  // Picks a voice that's already idle when one's available (true overlap,
  // no retrigger click on the sound already playing); falls back to a
  // round-robin voice (oldest-assigned) only once every voice in the pool
  // is genuinely busy at once, which a 4-voice pool should rarely hit for
  // a human's actual scroll rate.
  function pickCarouselVoice() {
    for (var i = 0; i < carouselTickVoices.length; i++) {
      var v = carouselTickVoices[i];
      if (v.paused || v.ended) { return v; }
    }
    var voice = carouselTickVoices[carouselTickVoiceCursor % carouselTickVoices.length];
    carouselTickVoiceCursor++;
    return voice;
  }

  function pickCarouselVariantIndex() {
    if (carouselTickUrls.length === 1) { return 0; }
    var idx;
    do {
      idx = Math.floor(Math.random() * carouselTickUrls.length);
    } while (idx === carouselLastVariantIndex);
    return idx;
  }

  function playCarouselTick() {
    // No variant pool loaded (asset list empty/failed) -- fall back to
    // the plain single-sample tick rather than staying silent.
    if (!carouselTickUrls.length || !carouselTickVoices.length) {
      playBrowse();
      return;
    }
    var now = Date.now();
    carouselRapidStreak = (now - carouselLastTickAt < CAROUSEL_RAPID_REPEAT_MS) ? (carouselRapidStreak + 1) : 0;
    carouselLastTickAt = now;
    var duck = Math.max(CAROUSEL_RAPID_DUCK_FLOOR, 1 - carouselRapidStreak * CAROUSEL_RAPID_DUCK_STEP);

    var variantIndex = pickCarouselVariantIndex();
    carouselLastVariantIndex = variantIndex;
    var url = carouselTickUrls[variantIndex];
    var voice = pickCarouselVoice();

    try {
      if (voice.src !== url) { voice.src = url; }
      voice.currentTime = 0;
      voice.volume = Math.max(0, Math.min(1, CAROUSEL_TICK_GAIN * duck));
      var rate = 1 + (Math.random() * 2 - 1) * CAROUSEL_TICK_PITCH_RANGE;
      voice.playbackRate = rate;
      voice.preservesPitch = false;
      voice.webkitPreservesPitch = false;
      voice.mozPreservesPitch = false;
      var p = voice.play();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }

  var tapeRack = null;
  var slotPrev = null;
  var slotCenter = null;
  var slotNext = null;
  var slotPrevImg = null;
  var slotNextImg = null;
  var rackUpBtn = null;
  var rackDownBtn = null;
  var tapeObject = null;
  var tapeFlip = null;
  var tapeImg = null;
  var sideSelect = null;
  var sideSelectA = null;
  var sideSelectB = null;
  var playBtn = null;
  var deckOverlayWrap = null; // clipping/positioning wrapper -- hidden/visible toggling happens here (see setOverlayShown())
  var deckOverlayImg = null; // the actual <img> inside the wrapper -- .src assignment only, see applyDeckOverlay()
  var hubLeft = null;
  var hubRight = null;
  var doorPopEl = null;
  var stageEl = null; // locked-aspect box every deck layer positions against -- see eva-radio.css's .eva-radio-widget__stage; also initDeckCalibration()'s coordinate space
  var tape = null; // manifest entry -- the currently FOCUSED/browsed tape, not necessarily what's playing

  // --- Phase 2 (2026-08-04): visibility model ---
  var insertCorridorEl = null;
  var rackHiddenForPlayback = false;
  var ARC_HEIGHT_FRAC = 0.16; // how far the insert flight bows off the straight line, as a fraction of the flight distance -- the "corridor" read

  // Fixed cycle order per Per's final spec -- one step per click, wraps
  // around. Front/back are cosmetic only; only sideA/sideB are valid
  // drag-into-deck faces (see isSideFace()).
  var FACE_ORDER = ['front', 'back', 'sideA', 'sideB'];
  var currentFace = 'front'; // which of tape.images.{front,back,sideA,sideB} the card is showing right now

  function isSideFace(face) {
    return face === 'sideA' || face === 'sideB';
  }

  function nextFace(face) {
    var i = FACE_ORDER.indexOf(face);
    return FACE_ORDER[(i + 1) % FACE_ORDER.length];
  }

  // The deck: what's ACTUALLY inserted and playing, tracked independent
  // of which tape is currently focused/browsed in the rack (see Per's
  // "the deck plays what's inside it, full stop" -- scrolling/clicking
  // must never touch this, only an explicit drag-insert or the natural
  // end-of-side flip can). null when nothing has ever been inserted.
  var deckTapeId = null;
  var deckSide = null; // 'a' | 'b'

  // Mirrors eva-radio.js's own seekHoldAction, kept in sync via the
  // eva:radio:seekstate event it dispatches from beginSeekHold()/
  // endSeekHold() -- lets the hubs spin fast-forward/backward while FF/
  // REW is actually held, independent of deckPlaying (see
  // updateDeckWindowEffects()'s mode priority).
  var currentSeekAction = null; // 'ff' | 'rew' | null

  function isFocusedInDeck() {
    return !!(tape && deckTapeId === tape.id);
  }

  var lastWheelAt = 0;
  var touchStartX = null;

  // The door hotspot only exists/means anything while the widget is
  // showing the front tape angle -- tracked from eva-radio.js's own
  // 'eva:radio:angle' event, not re-derived by polling. No separate
  // "mode" gates this anymore (removed per Per -- keys + tape both work
  // at once), so this is angle-only now.
  var currentAngleInteractive = true;
  var currentAngle = 'front';
  function doorInteractive() { return currentAngleInteractive; }

  // --- Inside-deck overlay -------------------------------------------------
  // New optional per-tape art: images.sideAInside / images.sideBInside,
  // photos of the tape's own reel/window as seen through the cassette
  // door once loaded. Shown at 50% opacity inside the door window (see
  // eva-radio.css --eva-deck-window-* vars) only while audio is actually
  // playing (not just "something inserted" -- Per: "stop/eject removes
  // the overlay"), only on the 'front' angle (the only one with a
  // calibrated door window today), and only for tapes/sides that supply
  // the art -- tapes without it get no overlay, nothing else changes.
  // Reflects the DECK's actual contents (deckTapeId/deckSide), not
  // whichever tape happens to be focused/browsed in the rack right now --
  // those can differ (Per: "the deck plays what's inside it, full stop").
  var deckPlaying = false;

  // Distinguishes STOP from PAUSE, which look identical via deckPlaying
  // alone (both leave it false). Per: pause keeps the inside-deck art
  // visible (frozen); stop fades it out. Starts true (nothing loaded/
  // playing yet); flips false the instant real playback starts, flips
  // back true only on an explicit stop (see the eva:radio:playstate
  // listener below) -- never touched by pause.
  var deckStopped = true;
  var overlayFadeTimer = null;
  var OVERLAY_FADE_MS = 400; // matches the CSS transition duration on .eva-radio-widget__deck-overlay

  function findTapeById(id) {
    var tapesList = allTapes();
    for (var i = 0; i < tapesList.length; i++) {
      if (tapesList[i].id === id) { return tapesList[i]; }
    }
    return null;
  }

  // shown=true snaps visible immediately (playback just started); shown=
  // false fades out over OVERLAY_FADE_MS then hides (matches Per's "on
  // stop, fade it out" -- pause never calls this with false, see
  // applyDeckOverlay()'s deckStopped check).
  function setOverlayShown(shown) {
    if (!deckOverlayWrap) { return; }
    clearTimeout(overlayFadeTimer);
    if (shown) {
      deckOverlayWrap.hidden = false;
      void deckOverlayWrap.offsetWidth; // force layout so the opacity transition actually runs from 0
      deckOverlayWrap.classList.add('eva-radio-widget__deck-overlay--visible');
    } else {
      deckOverlayWrap.classList.remove('eva-radio-widget__deck-overlay--visible');
      overlayFadeTimer = setTimeout(function () { deckOverlayWrap.hidden = true; }, OVERLAY_FADE_MS);
    }
  }

  function applyDeckOverlay() {
    if (!deckOverlayImg || !deckOverlayWrap) { return; }
    if (deckStopped || !deckTapeId || !deckSide || currentAngle !== 'front') {
      setOverlayShown(false);
      return;
    }
    var playingTape = findTapeById(deckTapeId);
    var images = playingTape && playingTape.images;
    var url = images && (deckSide === 'b' ? images.sideBInside : images.sideAInside);
    if (!url) {
      setOverlayShown(false);
      return;
    }
    if (deckOverlayImg.src !== url) { deckOverlayImg.src = url; }
    setOverlayShown(true);
  }

  // Spinning hubs: a generic "mechanism is running" cue, independent of
  // whether the tape has inside-window art at all. Round 2 (2026-07-23):
  // VISIBILITY is gated on "a tape is loaded on the front angle" alone
  // (deckTapeId/deckSide), NOT on deckPlaying -- Per's transport spec has
  // paused/stopped tapes leave the cogs frozen in view, only "no tape
  // inserted" hides them. ROTATION is one continuous Web Animations API
  // Animation per hub, created once and left running for the widget's
  // lifetime -- play/pause and playbackRate (including negative, for a
  // true reversed spin on REW) are adjusted in place rather than
  // recreating the animation on every state change, so it never loses
  // its current rotation position when frozen and resumed.
  var HUB_SPIN_MS = 1800; // one full rotation at normal PLAY speed
  var HUB_SEEK_RATE = 3.5; // FF/REW multiplier -- Per: "fast forward (~3-4x)"
  var reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  var hubLeftAnim = null;
  var hubRightAnim = null;

  // Both keyframes carry the same translate(-50%,-50%) the CSS default
  // uses to center the hub on --eva-hub-*-x/y (see eva-radio.css) -- a
  // running or paused Animation's own keyframe value REPLACES the
  // element's CSS transform outright rather than composing with it, so
  // without this the centering translate would vanish the instant the
  // animation attaches, undoing the round-5 anchor fix during PLAY/FF/
  // REW and reintroducing the down-right offset only while spinning.
  function ensureHubAnim(el) {
    if (!el || reducedMotion || typeof el.animate !== 'function') { return null; }
    return el.animate(
      [
        { transform: 'translate(-50%, -50%) rotate(0deg)' },
        { transform: 'translate(-50%, -50%) rotate(360deg)' }
      ],
      { duration: HUB_SPIN_MS, iterations: Infinity, easing: 'linear' }
    );
  }

  // mode: 'hidden' (no tape loaded) | 'frozen' (paused/stopped/loaded-but-
  // idle) | 'play' | 'ff' | 'rew'. Visibility always applies; rotation
  // control is skipped entirely under prefers-reduced-motion -- teeth
  // stay static, only the fade-in/out (CSS --visible) still runs.
  function setHubMotion(mode) {
    var visible = mode !== 'hidden';
    if (hubLeft) { hubLeft.classList.toggle('eva-radio-widget__hub--visible', visible); }
    if (hubRight) { hubRight.classList.toggle('eva-radio-widget__hub--visible', visible); }
    if (reducedMotion) { return; }
    if (!hubLeftAnim) { hubLeftAnim = ensureHubAnim(hubLeft); }
    if (!hubRightAnim) { hubRightAnim = ensureHubAnim(hubRight); }
    [hubLeftAnim, hubRightAnim].forEach(function (anim) {
      if (!anim) { return; }
      if (mode === 'hidden' || mode === 'frozen') {
        anim.pause();
      } else if (mode === 'ff') {
        anim.updatePlaybackRate(HUB_SEEK_RATE);
        anim.play();
      } else if (mode === 'rew') {
        anim.updatePlaybackRate(-HUB_SEEK_RATE);
        anim.play();
      } else {
        anim.updatePlaybackRate(1);
        anim.play();
      }
    });
  }

  function updateDeckWindowEffects() {
    var loaded = currentAngle === 'front' && !!deckTapeId && !!deckSide;
    var mode;
    if (!loaded) {
      mode = 'hidden';
    } else if (currentSeekAction === 'ff') {
      mode = 'ff';
    } else if (currentSeekAction === 'rew') {
      mode = 'rew';
    } else if (deckPlaying) {
      mode = 'play';
    } else {
      mode = 'frozen'; // covers PAUSE, STOP, and "loaded but never started"
    }
    setHubMotion(mode);
  }

  // Door-pop: a brief visual "catch releases" flash inside the door
  // window, fired the instant stop/eject happens -- see the
  // eva:radio:playstate listener below, which delays the tape's own
  // return-to-rack animation slightly so the two read as sequential
  // (door pops, then the cassette comes back out) rather than
  // simultaneous.
  function playDoorPop() {
    if (!doorPopEl) { return; }
    doorPopEl.classList.remove('eva-radio-widget__door-pop--popping');
    void doorPopEl.offsetWidth; // restart the CSS animation even if triggered twice in quick succession
    doorPopEl.classList.add('eva-radio-widget__door-pop--popping');
  }

  var dragging = false;
  var dragMoved = false;
  var dragStartX = 0;
  var dragStartY = 0;
  var activePointerId = null;
  var dragDeckRect = null; // deckFaceRect(), measured ONCE at pointerdown -- onPointerMove reads this cached value only, never re-measures, so the hot path stays free of layout-triggering DOM reads
  var dragNearDeck = false; // tracked so the near-deck visual class is only toggled on a real state change, not every single move event

  // Manifest schema v1 gives 4 independent image slots: front/back are
  // the J-card cover art (cosmetic cycle stops) and sideA/sideB are
  // photos of the physical cassette shell -- see FACE_ORDER above. The
  // key names match tape.images' own keys exactly, so rendering a face
  // is a direct lookup, no per-face branching needed.
  var FACE_LABEL = { front: 'cover', back: 'cover (back)', sideA: 'Side A', sideB: 'Side B' };

  function renderFace(face) {
    if (!tape || !tape.images || !tapeImg) { return; }
    var url = tape.images[face];
    if (!url) { return; }
    if (tapeImg.src !== url) { tapeImg.src = url; }
    tapeImg.alt = (tape.title || '') + ' -- ' + (FACE_LABEL[face] || face);
  }

  function preloadImages() {
    if (!tape || !tape.images) { return; }
    var images = tape.images;
    [images.front, images.back, images.sideA, images.sideB].forEach(function (src) {
      if (src) { var im = new Image(); im.src = src; }
    });
  }

  // Per's spec (2026-07-23): all six views of a tape (cover/back/sideA/
  // sideB/insideA/insideB) share ONE aspect ratio by design -- the tile
  // must hold CONSTANT dimensions through the whole cycle, image
  // swapped 1:1, no per-view resizing, no layout shift ("like turning a
  // cassette case in your hand"). So this only ever runs once per
  // FOCUSED TAPE (from whichever face loads first), never on every
  // cycle step -- see lockTapeAspect()'s call sites, not showFace().
  function lockTapeAspect() {
    if (!tapeImg || !tapeFlip) { return; }
    var setFromImg = function () {
      if (tapeImg.naturalWidth && tapeImg.naturalHeight) {
        tapeFlip.style.setProperty('--eva-tape-aspect', tapeImg.naturalWidth + ' / ' + tapeImg.naturalHeight);
        // Rack-misalignment root cause (2026-08-06, found via slow-3G
        // repro): slotCenter has no explicit width -- it shrinks to fit
        // tapeFlip, whose width is aspect-ratio-derived from THIS custom
        // property. Until a real tape cover image loads, tapeFlip sits on
        // the CSS fallback (800/505, LANDSCAPE) while every real cover is
        // portrait (memphis is ~1206/1885) -- a huge width delta. If
        // onTapesReady()/setFocusedTapeIndex() call syncRackAlignment()
        // before this image loads (near-certain on a slow connection,
        // since the tape-cover fetch races the much larger boombox
        // photo), it measures slotCenter at the WRONG (fallback-aspect)
        // width and bakes that into --eva-rack-align-x permanently --
        // nothing re-measured once the cover image (and therefore the
        // real width) actually arrived. Confirmed live under a throttled
        // (~400kbps) repro: the boombox photo's own load timing (radioImg
        // load/ResizeObserver, see the ResizeObserver setup below) had
        // ZERO effect on the misalignment, but re-running
        // syncRackAlignment() here -- right as the true aspect ratio
        // lands, whether synchronously (cached) or via the deferred
        // 'load' listener below -- fixes it every time.
        syncRackAlignment();
      }
    };
    if (tapeImg.complete) {
      setFromImg();
    } else {
      tapeImg.addEventListener('load', setFromImg, { once: true });
    }
  }

  // Sets the visible face directly, no turn animation -- used for
  // non-user-driven changes (initial render, refocusing a different
  // tape, resetting to front the instant a tape lands in the deck).
  // User-driven face changes go through advanceFace() below instead.
  // Deliberately does NOT touch the tile's aspect ratio -- see
  // lockTapeAspect() above.
  function showFace(face) {
    currentFace = face;
    renderFace(face);
    updateDragTooltip();
    updateSideSelectUI();
    updatePlayBtnUI();
  }

  // --- Side-select (Phase 2b, 2026-08-05) ---------------------------------
  // Shown only when a real tape is focused -- toggled via [hidden], not
  // opacity, so it's never a stray tab-stop when empty. Highlights
  // whichever side button matches currentFace; neither is highlighted
  // when showing front/back, which is correct (no side is "selected"
  // yet in that state -- the PLAY button/Enter fall back to Side A).
  function updateSideSelectUI() {
    if (!sideSelect) { return; }
    sideSelect.hidden = !tape;
    if (sideSelectA) { sideSelectA.classList.toggle('eva-radio-widget__side-select-btn--active', currentFace === 'sideA'); }
    if (sideSelectB) { sideSelectB.classList.toggle('eva-radio-widget__side-select-btn--active', currentFace === 'sideB'); }
  }

  // --- Explicit PLAY control (2026-08-06) ---------------------------------
  // Click/tap on the tape body is cycle-only now (see onPointerUp) -- Per's
  // fix for the old pendingTapToPlay flow, where the first tap on a
  // freshly-centered tape played it while every later tap just cycled
  // faces, an inconsistency the "click to turn the cassette over" tooltip
  // never signaled. Playback is explicit now: drag-to-insert (unchanged)
  // or this button, both funneling through the same playCenteredTape() ->
  // insertFocusedTape() call, so a PLAY-button insert runs the identical
  // corridor flight a drag-release does. Shown/hidden on the same "a real
  // tape is focused" gate as the side-select row.
  function updatePlayBtnUI() {
    if (!playBtn) { return; }
    playBtn.hidden = !tape;
  }

  function onSideSelectClick(side) {
    if (!tape) { return; }
    showFace(side === 'b' ? 'sideB' : 'sideA');
  }

  // Shared by the explicit PLAY control (playBtn click) and Enter-to-play
  // (onRackKeydown) -- the only two gestures that ever start playback from
  // the rack now that plain click/tap is cycle-only (see onPointerUp).
  // Loads/plays whichever side is currently showing (set via the cycle-tap
  // or the side-select buttons above), defaulting to Side A if the card is
  // still on front/back.
  function playCenteredTape() {
    if (!tape) { return; }
    if (!isSideFace(currentFace)) { showFace('sideA'); }
    var target = deckFaceRect();
    if (target) { insertFocusedTape(target.cx, target.cy); }
  }

  // --- Cosmetic face cycle (look only, never touches playback) -------------
  // Per's final spec: click/tap always advances the cycle front -> back
  // -> Side A -> Side B -> front, one step per press, regardless of
  // whether the focused tape happens to be the one currently in the
  // deck -- looking at another face doesn't, by itself, change what's
  // playing. Only an actual drag-insert while a SIDE face is showing
  // does that (see insertFocusedTape()). TURN_ANIM_MS mirrors the CSS
  // eva-tape-turn keyframe duration -- the image swap happens at the
  // animation's midpoint (card "edge-on"), so the visible change always
  // lands while the card reads as turning, not before or after it.
  var TURN_ANIM_MS = 260;

  function advanceFace() {
    if (!tape) { return; }
    var next = nextFace(currentFace);
    if (tapeFlip) {
      tapeFlip.classList.remove('eva-radio-widget__tape-flip--turning');
      void tapeFlip.offsetWidth; // restart the animation even if triggered again mid-turn
      tapeFlip.classList.add('eva-radio-widget__tape-flip--turning');
    }
    setTimeout(function () { showFace(next); }, TURN_ANIM_MS / 2);
    setTimeout(function () {
      if (tapeFlip) { tapeFlip.classList.remove('eva-radio-widget__tape-flip--turning'); }
    }, TURN_ANIM_MS);
  }

  // --- Door hit-testing ----------------------------------------------------

  function doorRect() {
    if (!doorInteractive()) { return null; }
    // One .eva-radio-hotspot--door polygon exists per angle SVG (front,
    // above), calibrated to that angle's own photo -- only one is ever
    // actually rendered at a time (the others sit under display:none from
    // applyAngle()). A plain querySelector always grabbed the FIRST one in
    // document order regardless of which angle was showing, so drag-insert
    // silently never hit a target on any angle but the front. Walk all of
    // them and use whichever one actually has a real, non-zero rect.
    var doorEls = document.querySelectorAll('.eva-radio-hotspot--door');
    for (var i = 0; i < doorEls.length; i++) {
      var r = doorEls[i].getBoundingClientRect();
      if (r.width && r.height) { return r; }
    }
    return null;
  }

  function pointOverlapsRect(x, y, r) {
    return r && x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  }

  // Phase 1b (2026-08-04) -- the old drop target was doorRect() directly:
  // one small SVG polygon calibrated tight to the cassette-slot artwork
  // (see doorRect()'s own comment), easy to miss by a few px and get
  // stranded with no failure path. "The whole deck face" isn't a
  // separately calibrated hotspot anywhere in the SVGs -- there's nothing
  // to reuse -- so this expands the door rect's own bounding box around
  // its center instead of inventing new per-angle polygon art (which
  // would mean touching the calibrated SVGs the unit itself is built
  // from, not just hit-testing logic). DECK_FACE_SCALE_X/Y are a
  // judgment call, not a measurement -- generous on purpose, easy to
  // retune (just two numbers) once Per's actually tried it on his phone.
  function deckFaceRect() {
    var door = doorRect();
    if (!door) { return null; }
    var cx = door.left + door.width / 2;
    var cy = door.top + door.height / 2;
    var w = door.width * DECK_FACE_SCALE_X;
    var h = door.height * DECK_FACE_SCALE_Y;
    return {
      left: cx - w / 2,
      right: cx + w / 2,
      top: cy - h / 2,
      bottom: cy + h / 2,
      width: w,
      height: h,
      cx: cx,
      cy: cy
    };
  }

  // Distance from a point to the nearest edge of a rect -- 0 if the point
  // is already inside it. Pure arithmetic against an already-measured
  // rect (no DOM read), safe to call every pointermove.
  function distanceToRect(x, y, r) {
    var dx = Math.max(r.left - x, 0, x - r.right);
    var dy = Math.max(r.top - y, 0, y - r.bottom);
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Hidden once the focused tape is the one actually in the deck -- its
  // text would be misleading (dragging it again just re-inserts the same
  // side it's already playing, not a new action worth prompting for).
  // Also toggles the in-deck dim mark -- Per: "while a tape is inserted
  // in the deck, its rack slot shows the front cover (dimmed or marked
  // 'in deck')" -- both driven by the same isFocusedInDeck() check.
  function updateDragTooltip() {
    if (!tapeObject) { return; }
    var inDeck = isFocusedInDeck();
    tapeObject.classList.toggle('eva-radio-widget__tape-object--in-deck', inDeck);
    if (inDeck) {
      tapeObject.removeAttribute('data-eva-tooltip');
      if (window.EvaRadioWidget && window.EvaRadioWidget.hideTooltip) { window.EvaRadioWidget.hideTooltip(); }
    } else if (isSideFace(currentFace)) {
      tapeObject.setAttribute('data-eva-tooltip', 'Put the cassette in the tape deck to play it');
    } else {
      tapeObject.setAttribute('data-eva-tooltip', 'Click to turn the cassette over');
    }
  }

  // --- Insert: the ONLY gesture that ever changes playback ----------------
  // Per: only Side A/Side B are drag-into-deck targets -- front/back
  // spring back (see onPointerUp's isSideFace() guard, which is what
  // keeps this from ever being called with a cover face showing).
  // Whichever side is currently up (currentFace) decides what plays.

  // Positions the corridor streak (a 1px-wide div, transform-origin left
  // center) to span from (x1,y1) to (x2,y2) in viewport space via
  // translate+rotate+scaleX -- same trig any "connect two points with a
  // div" technique uses. Shown for the flight's duration only.
  var corridorHideTimer = null;
  function showInsertCorridor(x1, y1, x2, y2, duration) {
    if (!insertCorridorEl) { return; }
    clearTimeout(corridorHideTimer);
    var dx = x2 - x1;
    var dy = y2 - y1;
    var length = Math.sqrt(dx * dx + dy * dy);
    var angle = Math.atan2(dy, dx) * (180 / Math.PI);
    insertCorridorEl.style.transform = 'translate(' + x1 + 'px,' + y1 + 'px) rotate(' + angle + 'deg) scaleX(' + length + ')';
    void insertCorridorEl.offsetWidth; // force layout so the opacity transition actually runs from 0
    insertCorridorEl.classList.add('eva-radio-widget__insert-corridor--visible');
    corridorHideTimer = setTimeout(function () {
      insertCorridorEl.classList.remove('eva-radio-widget__insert-corridor--visible');
    }, duration);
  }

  // --- Insert flight animation ---------------------------------------------
  // Phase 2 (2026-08-04): replaces the earlier straight CSS-transition fly
  // (a plain translate(dx,dy) scale) with a genuine arced flight via the
  // Web Animations API -- the same API ensureHubAnim() already uses
  // elsewhere in this file, not a new pattern. A dead-straight line read
  // as "teleporting", not "traveling through a corridor" -- the arc bulge
  // (ARC_HEIGHT_FRAC of the flight distance, perpendicular to the
  // straight line) plus the corridor streak underneath it together are
  // what make this read as a lane between the rack and the deck, not
  // just an opacity fade. Still ends on the exact same dx/dy the old
  // version computed, so the deck target math (insertFocusedTape's
  // caller, deckFaceRect()) is untouched.
  var insertFlightAnim = null;

  function insertFocusedTape(targetX, targetY) {
    if (!tape || !isSideFace(currentFace)) { return; }
    playBrowse(); // layered just before the insert mechanicals (mechPlayAudio fires later, after the flight duration + PLAY_SOUND_LEAD_MS)
    var rect = tapeObject.getBoundingClientRect();
    var startX = rect.left + rect.width / 2;
    var startY = rect.top + rect.height / 2;
    var dx = 0, dy = 0;
    if (typeof targetX === 'number') {
      dx = targetX - startX;
      dy = targetY - startY;
    }

    // dist also drives the flight DURATION now (Phase 2b) -- see
    // INSERT_SPEED_PX_PER_MS's comment -- computed once here and reused
    // below for both the corridor streak and the arc bulge math that
    // already existed.
    var dist = Math.sqrt(dx * dx + dy * dy) || 1;
    var duration = insertDurationForDistance(dist);

    if (typeof targetX === 'number') { showInsertCorridor(startX, startY, targetX, targetY, duration); }

    // Perpendicular unit vector to the straight (dx,dy) line, scaled by
    // ARC_HEIGHT_FRAC of the flight distance -- the arc's own "bulge".
    // Always picks the perpendicular direction that points upward
    // (negative Y) so the flight reads as a gentle rack-to-deck lane
    // arcing up and over, not a random wobble direction.
    var ux = dx / dist, uy = dy / dist; // unit vector along the straight flight
    var px = -uy, py = ux; // rotate 90deg for the perpendicular
    if (py > 0) { px = -px; py = -py; } // always bulge upward
    var bulge = dist * ARC_HEIGHT_FRAC;
    var midDx = dx * 0.5 + px * bulge;
    var midDy = dy * 0.5 + py * bulge;

    if (tapeObject.animate) {
      if (insertFlightAnim) { insertFlightAnim.cancel(); }
      tapeObject.style.transition = 'none'; // let WAAPI own transform/opacity for the flight -- no fighting with the CSS transitions used elsewhere on this element
      insertFlightAnim = tapeObject.animate([
        { transform: 'translate(0px,0px) scale(1)', opacity: 1 },
        { transform: 'translate(' + midDx + 'px,' + midDy + 'px) scale(0.7)', opacity: 0.85 },
        { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(0.35)', opacity: 0 }
      ], { duration: duration, easing: 'ease-in', fill: 'forwards' });
    } else {
      // No Animation API (very old browser) -- fall back to the plain
      // CSS-transition fly the class below already defines.
      tapeObject.style.setProperty('--eva-tape-insert-dx', dx + 'px');
      tapeObject.style.setProperty('--eva-tape-insert-dy', dy + 'px');
      tapeObject.classList.add('eva-radio-widget__tape-object--inserting');
    }

    var tapeId = tape.id;
    var sideToPlay = currentFace === 'sideB' ? 'b' : 'a';

    setTimeout(function () {
      deckTapeId = tapeId;
      deckSide = sideToPlay;
      showFace('front'); // Per: "the moment playback starts, the rack image resets to the front cover" -- also refreshes the tooltip/in-deck mark now that isFocusedInDeck() just changed
      applyDeckOverlay(); // ahead of the playstate event so the overlay is already correct the instant audio.play() fires
      updateDeckWindowEffects();
      playClunk(); // seat-in feel, right as the cassette visually lands -- the separate play-clack (eva-radio.js) follows a few hundred ms later once playback actually starts
      if (window.EvaRadioWidget && window.EvaRadioWidget.playTape) {
        window.EvaRadioWidget.playTape(tapeId, sideToPlay);
      }
      // The tape stays visible once inserted -- pressing it still only
      // advances the cosmetic cycle, dragging a side face in again re-inserts.
      if (insertFlightAnim) { insertFlightAnim.cancel(); insertFlightAnim = null; }
      tapeObject.classList.remove('eva-radio-widget__tape-object--inserting');
      tapeObject.style.transition = '';
      tapeObject.style.transform = '';
      dragMoved = false;
    }, duration);
  }

  // --- Rack scroll (3 fixed-SIZE slots -- see eva-radio.css) ---------------
  // The center DOM slot is PERMANENTLY the large/interactive one and
  // prev/next are PERMANENTLY the small preview ones -- size no longer
  // travels with "which slot is active" the way v1 did. That earlier
  // design let the *content* stay fixed per DOM slot while the active/
  // empty (and therefore large/small) role swapped between slots on
  // scroll, so the large card could end up at the top or bottom of the
  // rack instead of staying centered -- exactly the "wrong tape grows"
  // bug Per flagged. Now scrolling instead moves focusedTapeIndex (a
  // position in the tapes array, sorted dateAdded desc), and it's
  // CONTENT that would shift between slots once there's more than one
  // tape to preview in prev/next. Until then, with tapes.length === 1,
  // there's nothing adjacent to move to -- see updateArrowDisabled().

  // Per's sizing rule: the center card's rendered height always matches
  // the radio photo's own rendered height exactly -- not a fixed CSS
  // percentage of the widget's width, which drifted from the radio's
  // actual size on resize and across angle photos of different aspect
  // ratios. Prev/next are a smaller fixed fraction of that same height --
  // NEIGHBOR_SCALE is also duplicated as a literal 0.6 in eva-radio.css's
  // calc() for the neighbor slots' height (see .eva-radio-widget__tape-
  // slot's own comment) and in animateRackTransition()'s WAAPI grow/
  // shrink math below -- keep all three in sync if this ever changes.
  var NEIGHBOR_SCALE = 0.6;
  var radioImg = null;
  var rackResizeObserver = null;
  var focusedTapeIndex = 0; // index into eva-radio.js's tapes array, not a DOM slot

  // Live-resize desync fix (2026-08-06): this used to set slotCenter/
  // slotPrev/slotNext's .style.height individually, each one filtered
  // through .eva-radio-widget__tape-slot's own `transition: height`.
  // During a fast CONTINUOUS drag-resize, syncRackSizing() re-fires many
  // times a second (via the ResizeObserver below, plus the live-resize
  // rAF loop), retargeting that transition on every call -- the actual
  // rendered height could never catch up, chasing a constantly-moving
  // target and visibly decoupling from the boombox photo's own (un-
  // transitioned, instant) size -- confirmed live in Per's screen
  // recording as the center tape at times rendering LARGER than the
  // radio itself. Fixed by driving ONE plain (non-@property, therefore
  // never transitioned) custom property instead: --eva-rack-tape-h, set
  // here on tapeRack, read by both the active and neighbor slot height
  // rules in eva-radio.css via var()/calc() -- a single scale source
  // that always applies instantly, on every call, matching the
  // boombox's own zero-lag resize behavior exactly.
  function syncRackSizing() {
    if (!radioImg || !tapeRack) { return; }
    var radioHeight = radioImg.getBoundingClientRect().height;
    if (!radioHeight) { return; } // image not laid out/loaded yet
    tapeRack.style.setProperty('--eva-rack-tape-h', radioHeight + 'px');
  }

  // Aligns the rack's centre slot to the DECK'S actual x-centre (the
  // cassette door hotspot -- the same reference point deckFaceRect()
  // already treats as "the deck" for the insert-flight target), not the
  // widget's own geometric centre. Per's own sketch has the rack sitting
  // centred under the deck so the insert corridor reads as a straight
  // vertical path, but .eva-radio-widget__tape-rack's plain
  // `justify-content: center` only centres the row within its own box --
  // correct only if the deck happened to sit dead-centre in the photo
  // itself, which it doesn't (the boombox art isn't symmetric; the door
  // measures a few % off the photo's true horizontal centre on both
  // angles). Applied as a small corrective `transform: translateX()` on
  // top of the existing flex centering (kept as the base/fallback) rather
  // than replacing it with absolute positioning -- one CSS custom
  // property (--eva-rack-align-x) so it composes with the unrelated
  // translateY the rack's own hidden-playing state already uses (see
  // eva-radio.css), instead of two transforms fighting over the same
  // property.
  //
  // Idempotent by construction: reads the rack's CURRENT rendered centre
  // (which already reflects whatever offset is live right now) and backs
  // that offset back out before computing the new one, so calling this
  // repeatedly -- resize, angle swap, tape-data-ready -- never compounds
  // a stale correction on top of itself.
  function syncRackAlignment() {
    if (!tapeRack || !slotCenter) { return; }
    var door = doorRect();
    if (!door || !door.width) {
      tapeRack.style.removeProperty('--eva-rack-align-x'); // widget minimized/not laid out -- fall back to plain flex centering
      return;
    }
    var doorCenterX = door.left + door.width / 2;
    var currentOffset = parseFloat(getComputedStyle(tapeRack).getPropertyValue('--eva-rack-align-x')) || 0;
    var slotRect = slotCenter.getBoundingClientRect();
    var naturalCenterX = (slotRect.left + slotRect.width / 2) - currentOffset;
    tapeRack.style.setProperty('--eva-rack-align-x', (doorCenterX - naturalCenterX) + 'px');
  }

  // --- Live-resize tracking (2026-08-06) -----------------------------------
  // The ResizeObserver below (see init) already re-runs syncRackSizing()/
  // syncRackAlignment() on most size changes, but a fast, continuous
  // drag-resize can outrun it -- ResizeObserver callbacks are batched to
  // roughly once per frame AFTER layout, and on top of that the RACK's
  // own `transform: translateX(--eva-rack-align-x)` is CSS-transitioned
  // (0.35s, for the legitimate hidden-while-playing slide) -- retargeted
  // every single call during a fast drag, it visibly lagged and drifted
  // off the door's true centerline (Per's bug report #2) even though the
  // underlying math was correct each time. An rAF loop, running only
  // while eva-radio.js reports an active resize gesture, is the explicit
  // "track continuously, not just on settle" driver Per asked for;
  // toggling --live-resize on tapeRack alongside it suppresses that
  // transition for the gesture's duration so every correction lands
  // instantly instead of chasing a moving target.
  var liveResizeActive = false;
  var liveResizeRafId = null;

  function liveResizeTick() {
    syncRackSizing();
    syncRackAlignment();
    if (liveResizeActive) {
      liveResizeRafId = requestAnimationFrame(liveResizeTick);
    }
  }

  function onRadioResizeGesture(e) {
    var active = !!(e.detail && e.detail.active);
    if (active) {
      if (liveResizeActive) { return; } // already running -- ignore a stray duplicate start
      liveResizeActive = true;
      if (tapeRack) { tapeRack.classList.add('eva-radio-widget__tape-rack--live-resize'); }
      liveResizeRafId = requestAnimationFrame(liveResizeTick);
    } else {
      if (!liveResizeActive) { return; }
      liveResizeActive = false;
      if (liveResizeRafId) { cancelAnimationFrame(liveResizeRafId); liveResizeRafId = null; }
      // One final sync WHILE still transition-suppressed, so the gesture
      // ends on the exact right position with no lag -- only THEN restore
      // the transition, so any later, non-gesture change (a tape switch,
      // playback starting) still animates smoothly as before.
      syncRackSizing();
      syncRackAlignment();
      if (tapeRack) { tapeRack.classList.remove('eva-radio-widget__tape-rack--live-resize'); }
    }
  }

  // --- Rack navigation motion (2026-08-05, smoothing pass) ----------------
  // Roles (prev/center/next) are fixed DOM slots -- only their CONTENT
  // (image src, set elsewhere) moves between them on navigation, the
  // boxes themselves never do (see the big comment above). To read as one
  // continuous coverflow motion rather than an instant snap, every
  // navigation kicks a WAAPI transform+opacity "entrance" on all three
  // slots at once, all sharing the same duration/easing so they settle
  // together -- Per: "the row moves as one thing." Each slot's entrance
  // approximates where its NEW content visually was a moment ago: the
  // slot growing into center enters from the neighbor position it came
  // from (small -> full size), the slot shrinking into a neighbor enters
  // from the center position it came from (full size -> small), and the
  // slot receiving a genuinely new (previously offscreen) tape just
  // slides in a short distance and fades. transform/opacity only -- never
  // a layout-triggering property -- so this stays compositor-only no
  // matter how big the momentary scale-up/down goes.
  var RACK_ANIM_MS = 260; // within Per's 220-280ms ask
  var RACK_ANIM_EASING = 'cubic-bezier(0.22, 1, 0.36, 1)'; // ease-out
  var RACK_ENTRY_EXTRA_PX = 18; // brand-new-content roles have no real "from" position to match -- just a short slide + fade
  var rackSlotAnims = { center: null, prev: null, next: null };

  function rackAnimTarget(role) {
    if (role === 'center') { return slotCenter; }
    return role === 'prev' ? slotPrev : slotNext;
  }

  // Repeated fast presses must retarget the in-flight animation, not
  // queue behind it or snap back to the nominal "just arrived" offset
  // first (that snap-back IS the "restarts from a standstill" jank) --
  // so when a role is already mid-animation, read its LIVE computed
  // transform/opacity before cancelling and use that as the new
  // animation's start point instead of the fresh nominal one.
  function animateRackSlot(role, fromTransform, fromOpacity) {
    var el = rackAnimTarget(role);
    if (!el || !el.animate || reducedMotion) { return; }
    var startTransform = fromTransform;
    var startOpacity = fromOpacity;
    var existing = rackSlotAnims[role];
    if (existing) {
      var live = window.getComputedStyle(el);
      startTransform = (live.transform && live.transform !== 'none') ? live.transform : fromTransform;
      var liveOpacity = parseFloat(live.opacity);
      startOpacity = isNaN(liveOpacity) ? fromOpacity : liveOpacity;
      existing.cancel();
    }
    var anim = el.animate([
      { transform: startTransform, opacity: startOpacity },
      { transform: 'translateX(0) scale(1)', opacity: 1 }
    ], { duration: RACK_ANIM_MS, easing: RACK_ANIM_EASING, fill: 'forwards' });
    rackSlotAnims[role] = anim;
    anim.addEventListener('finish', function () {
      if (rackSlotAnims[role] === anim) { rackSlotAnims[role] = null; }
    });
    anim.addEventListener('cancel', function () {
      if (rackSlotAnims[role] === anim) { rackSlotAnims[role] = null; }
    });
  }

  function rackSlotCenterX(el) {
    if (!el) { return 0; }
    var r = el.getBoundingClientRect();
    return r.left + r.width / 2;
  }

  // direction: +1 = moved to the next tape, -1 = moved to the previous one.
  function animateRackTransition(direction) {
    if (reducedMotion || !slotCenter || !slotPrev || !slotNext) { return; }
    var centerX = rackSlotCenterX(slotCenter);
    var prevX = rackSlotCenterX(slotPrev);
    var nextX = rackSlotCenterX(slotNext);
    var growScale = NEIGHBOR_SCALE; // the incoming center content was neighbor-sized a moment ago
    var shrinkScale = 1 / NEIGHBOR_SCALE; // the incoming neighbor content was center-sized a moment ago

    if (direction === 1) {
      animateRackSlot('center', 'translateX(' + (nextX - centerX) + 'px) scale(' + growScale + ')', 0.75);
      animateRackSlot('prev', 'translateX(' + (centerX - prevX) + 'px) scale(' + shrinkScale + ')', 0.75);
      animateRackSlot('next', 'translateX(' + RACK_ENTRY_EXTRA_PX + 'px) scale(0.85)', 0);
    } else {
      animateRackSlot('center', 'translateX(' + (prevX - centerX) + 'px) scale(' + growScale + ')', 0.75);
      animateRackSlot('next', 'translateX(' + (centerX - nextX) + 'px) scale(' + shrinkScale + ')', 0.75);
      animateRackSlot('prev', 'translateX(' + (-RACK_ENTRY_EXTRA_PX) + 'px) scale(0.85)', 0);
    }
  }

  function allTapes() {
    return (window.EvaRadioWidget && window.EvaRadioWidget.getTapes && window.EvaRadioWidget.getTapes()) || [];
  }

  function tapeCount() {
    return allTapes().length || 1;
  }

  function updateArrowDisabled() {
    var count = tapeCount();
    if (rackUpBtn) { rackUpBtn.disabled = focusedTapeIndex <= 0 || count <= 1; }
    if (rackDownBtn) { rackDownBtn.disabled = focusedTapeIndex >= count - 1 || count <= 1; }
  }

  // Neighbor preview: a real thumbnail of the adjacent tape's cover when
  // one exists, and NOTHING AT ALL when there isn't one (2026-08-06,
  // Per) -- the rack is explicitly no-wrap with clean ends, so at the
  // first/last tape the outward slot renders empty rather than showing
  // the old dashed placeholder. That placeholder element is deleted from
  // snippets/eva-radio.liquid entirely, hence no placeholderEl parameter
  // here any more; the empty space is held open by the slot's own fixed
  // width in assets/eva-radio.css, so the centre tape never moves.
  //
  // The old guard was `if (!imgEl || !placeholderEl) return;` -- had the
  // markup been removed without touching this, that null placeholder
  // would have early-returned and silently killed neighbour previews
  // outright, not just the empty-slot case.
  //
  // Phase 2: also toggles the slot's own interactive affordance
  // (--interactive class + tabindex/aria-label) so a tap/Enter only ever
  // does something when there's a REAL neighbor tape to center on --
  // slotEl itself is the whole tappable target.
  function renderNeighborSlot(slotEl, imgEl, neighborTape) {
    if (!imgEl) { return; }
    var url = neighborTape && neighborTape.images && neighborTape.images.front;
    if (url) {
      imgEl.hidden = false;
      if (imgEl.src !== url) { imgEl.src = url; }
      imgEl.alt = neighborTape.title || '';
    } else {
      imgEl.hidden = true;
      imgEl.removeAttribute('src'); // don't keep a stale cover decoded behind a hidden img
      imgEl.alt = '';
    }
    if (slotEl) {
      var interactive = !!neighborTape;
      slotEl.classList.toggle('eva-radio-widget__tape-slot--interactive', interactive);
      if (interactive) {
        slotEl.setAttribute('tabindex', '0');
        slotEl.setAttribute('role', 'button');
        slotEl.setAttribute('aria-label', 'Bring "' + (neighborTape.title || 'this tape') + '" to center');
      } else {
        slotEl.removeAttribute('tabindex');
        slotEl.removeAttribute('role');
        slotEl.removeAttribute('aria-label');
      }
    }
  }

  function renderNeighborPreviews() {
    var tapesList = allTapes();
    renderNeighborSlot(slotPrev, slotPrevImg, tapesList[focusedTapeIndex - 1]);
    renderNeighborSlot(slotNext, slotNextImg, tapesList[focusedTapeIndex + 1]);
  }

  // Tap-to-centre: a tap/click/Enter on a neighbor slot that currently
  // holds a real tape brings it to center (setFocusedTapeIndex) and
  // nothing else -- per Per's 2026-08-06 rule, a side tap ONLY recenters,
  // it never arms a follow-up play (that was the old pendingTapToPlay
  // flow, removed: the first tap on a freshly-centered tape used to play
  // it while every later tap just cycled faces, an inconsistency the
  // "click to turn the cassette over" tooltip never signaled). Does
  // nothing on an empty end-of-rack slot -- nothing to center to.
  function onNeighborSlotTap(delta) {
    var tapesList = allTapes();
    var targetIndex = focusedTapeIndex + delta;
    if (!tapesList[targetIndex]) { return; }
    setFocusedTapeIndex(targetIndex);
  }

  // Browsing NEVER touches playback -- per Per, "the deck plays what's
  // inside it, full stop." This only moves which tape's cover is
  // focused/interactive in the center slot (and which neighbors preview
  // above/below); deckTapeId/deckSide are untouched, so whatever's
  // actually playing keeps playing right through a scroll.
  function setFocusedTapeIndex(index) {
    var tapesList = allTapes();
    var count = tapesList.length || 1;
    index = Math.max(0, Math.min(count - 1, index));
    if (index === focusedTapeIndex) { return; } // no real change -- naturally throttles the carousel tick to once per change
    var direction = index > focusedTapeIndex ? 1 : -1;
    focusedTapeIndex = index;
    playCarouselTick();

    tape = tapesList[focusedTapeIndex] || tape;
    showFace('front'); // newly-focused tape always starts showing its cover, regardless of any peek state left on the previous one
    lockTapeAspect(); // a different tape's own six views may share a different ratio than the last -- recompute once per focus change, never mid-cycle
    preloadImages();
    updateDragTooltip();
    renderNeighborPreviews();
    updateArrowDisabled();
    animateRackTransition(direction);
  }

  function onRackWheel(e) {
    e.preventDefault();
    var now = Date.now();
    if (now - lastWheelAt < WHEEL_COOLDOWN_MS) { return; }
    lastWheelAt = now;
    setFocusedTapeIndex(focusedTapeIndex + (e.deltaY > 0 ? 1 : -1));
  }

  // Inertia (2026-08-05): a fast flick should travel further than a slow
  // drag, same as any real coverflow. touchSamples records recent
  // {x, t} points during the drag (trimmed to the last
  // TOUCH_VELOCITY_WINDOW_MS so an early slow start doesn't dilute the
  // velocity read of a gesture that sped up right before release);
  // onRackTouchEnd turns the resulting px/ms velocity into extra steps
  // on top of the base 1-step swipe.
  var touchSamples = [];
  var TOUCH_VELOCITY_WINDOW_MS = 120;
  var TOUCH_SWIPE_THRESHOLD_PX = 24;
  var TOUCH_INERTIA_PX_PER_MS_PER_STEP = 0.5; // tuned by feel, not measured -- raise to require a faster flick per extra step
  var TOUCH_INERTIA_MAX_EXTRA_STEPS = 3;

  function onRackTouchStart(e) {
    if (e.target.closest && e.target.closest('#EvaRadioTapeObject')) { touchStartX = null; touchSamples = []; return; } // let the tape's own gesture own this
    touchStartX = e.touches[0].clientX; // horizontal swipe now (2026-08-05 coverflow rebuild) -- was clientY
    touchSamples = [{ x: touchStartX, t: e.timeStamp }];
  }

  function onRackTouchMove(e) {
    if (touchStartX === null) { return; }
    touchSamples.push({ x: e.touches[0].clientX, t: e.timeStamp });
    while (touchSamples.length > 2 && (e.timeStamp - touchSamples[0].t) > TOUCH_VELOCITY_WINDOW_MS) {
      touchSamples.shift();
    }
  }

  function onRackTouchEnd(e) {
    if (touchStartX === null) { return; }
    var endX = (e.changedTouches && e.changedTouches[0].clientX) || touchStartX;
    var dx = touchStartX - endX;
    var samples = touchSamples;
    touchStartX = null;
    touchSamples = [];
    if (Math.abs(dx) < TOUCH_SWIPE_THRESHOLD_PX) { return; } // swipe threshold
    var velocity = 0; // px/ms, positive = swiping toward "next"
    if (samples.length >= 2) {
      var first = samples[0];
      var last = samples[samples.length - 1];
      var dt = last.t - first.t;
      if (dt > 0) { velocity = (first.x - last.x) / dt; }
    }
    var extraSteps = Math.min(TOUCH_INERTIA_MAX_EXTRA_STEPS, Math.floor(Math.abs(velocity) / TOUCH_INERTIA_PX_PER_MS_PER_STEP));
    var steps = 1 + extraSteps;
    setFocusedTapeIndex(focusedTapeIndex + (dx > 0 ? steps : -steps));
  }

  // Keyboard nav (Phase 2) -- bound to the rack element itself, not
  // document, so arrow keys only move the carousel while the rack
  // actually has focus (tab into it, or click/tap it) and never hijack
  // page-scroll arrow keys anywhere else. Up/Left = previous (toward
  // index 0, older-first per eva-radio.js's ascending dateAdded sort --
  // matches the existing Up-button direction exactly), Down/Right = next.
  function onRackKeydown(e) {
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setFocusedTapeIndex(focusedTapeIndex - 1);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      setFocusedTapeIndex(focusedTapeIndex + 1);
    } else if (e.key === 'Enter') {
      // Per spec: "enter loads the centred tape". Previously Enter only
      // worked on the neighbor-slot buttons (onNeighborSlotTap, to
      // center them) -- a keyboard-only user (Tab into the rack, arrow
      // to browse) had no way to actually load a tape at all, since
      // arrow-navigation never centers a tape ready to auto-play. Enter
      // is (along with the PLAY button) one of the deliberate, explicit
      // ways to start playback -- unlike plain click/tap, which is
      // cycle-only (see onPointerUp).
      if (e.target === sideSelectA || e.target === sideSelectB || e.target === slotPrev || e.target === slotNext || e.target === playBtn) { return; } // let those elements' own handlers own Enter
      e.preventDefault();
      playCenteredTape();
    }
  }

  // --- Phase 2 visibility model --------------------------------------------
  // "Tapes shown on open, hidden while playing, door click to bring them
  // back" -- the rack auto-hides (slide+fade, see the CSS class) the
  // instant playback starts, regardless of whether the user had it open;
  // clicking the door again (toggleRackFromDoor(), below) reveals it
  // without touching playback at all. Reopening the whole widget chassis
  // (eva-radio.js's restoreWidget()) always shows the rack fresh -- see
  // the eva:radio:opened listener below.
  //
  // A separate rack-hatch tab used to cover this same "bring it back"
  // job; removed 2026-08-26 once the door's own open/close toggle
  // (below) made it fully redundant.
  function hideRackForPlayback() {
    if (!tapeRack || rackHiddenForPlayback) { return; }
    rackHiddenForPlayback = true;
    tapeRack.classList.add('eva-radio-widget__tape-rack--hidden-playing');
  }

  function showRack() {
    if (!tapeRack || !rackHiddenForPlayback) { return; }
    rackHiddenForPlayback = false;
    tapeRack.classList.remove('eva-radio-widget__tape-rack--hidden-playing');
  }

  // Door-click toggle (2026-08-09): the door hotspot's own click now
  // opens OR closes the rack depending on its current state, reusing
  // showRack()/hideRackForPlayback() verbatim rather than a third
  // hide/show implementation -- the "-playing" half of that function's
  // name is legacy (it originally only ever fired from playback
  // starting); the flag and CSS class it flips are just "is the rack
  // hidden," which is exactly what a manual close needs too. Every
  // other close/open path (auto-hide on playback start,
  // eva:radio:opened) is untouched -- this only adds a second way IN to
  // the same shared state, per Per: "keep whatever other close paths
  // already exist; this adds one, removes none."
  //
  // Never fights drag-to-insert: the door hotspot (eva-radio.js,
  // dispatches eva:radio:door:pressed on its own delegated 'click'
  // listener) and the cassette (tapeObject, this file, its own
  // pointerdown/setPointerCapture below) are different elements on
  // different listeners entirely. A drag that starts on the tape calls
  // tapeObject.setPointerCapture() at pointerdown, so every pointer
  // event for that gesture -- including the click DOM would otherwise
  // synthesize on release -- targets tapeObject, never the door hotspot
  // underneath, regardless of where the drag ends. There is no shared
  // code path for this toggle to accidentally intercept.
  function toggleRackFromDoor() {
    if (rackHiddenForPlayback) { showRack(); } else { hideRackForPlayback(); }
  }

  // --- Pointer interaction on the tape itself -------------------------------
  // Same rule for mouse and touch: click/tap (no real drag movement) =
  // advance the cosmetic face cycle, ALWAYS -- never plays, regardless of
  // whether the focused tape happens to be the one in the deck. A real
  // drag that ends over the cassette door = insert, but only while a
  // SIDE face (Side A/Side B) is showing -- front/back spring back
  // instead (see onPointerUp).
  //
  // Drag is always available, even for a tape already in the deck --
  // cycling to the other side, then dragging it in again, is how you
  // deliberately switch sides (clicking alone never does).

  // Drag-lag diagnosis (2026-08-04): the pointer-follow itself was ALREADY
  // transform-only (translate(dx,dy), nothing else) and onPointerMove had
  // no layout-triggering read in it -- both already correct going in. The
  // actual cause was the missing compositor layer: without will-change,
  // the browser doesn't necessarily promote the tape to its own layer
  // until partway through the gesture, so the opening frames could still
  // force a real paint of the tape's full-resolution source photo (some
  // of these run several MB at ~1750px) instead of just repositioning an
  // already-composited layer. Fixed by toggling
  // .eva-radio-widget__tape-object--dragging (eva-radio.css) at
  // pointerdown -- before any movement, so the layer exists before the
  // first move event -- and removing it on every drag-ending path.
  //
  // dragDeckRect is measured ONCE here too, not per-move -- see
  // deckFaceRect()'s own comment for why "the whole deck face" isn't a
  // pre-existing calibrated hotspot.
  var dragTapeRect = null; // tapeObject's own rest-position rect, measured once alongside dragDeckRect -- lets the magnetic pull below compute an absolute target without any further DOM read

  function onPointerDown(e) {
    dragging = true;
    dragMoved = false;
    activePointerId = e.pointerId;
    dragStartX = e.clientX;
    dragStartY = e.clientY;
    dragDeckRect = deckFaceRect();
    dragTapeRect = tapeObject.getBoundingClientRect();
    dragNearDeck = false;
    tapeObject.classList.add('eva-radio-widget__tape-object--dragging');
    try { tapeObject.setPointerCapture(e.pointerId); } catch (err) {}
  }

  function onPointerMove(e) {
    if (!dragging || e.pointerId !== activePointerId) { return; }
    var dx = e.clientX - dragStartX;
    var dy = e.clientY - dragStartY;
    if (!dragMoved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      dragMoved = true;
      tapeObject.style.transition = 'none'; // live-follow the pointer while actually dragging
    }
    if (!dragMoved) { return; }

    // Magnetic snap (phase 1b): within SNAP_MARGIN_PX of the deck face,
    // blend the tape's target position toward the deck's own center
    // instead of following the raw pointer 1:1, so it reads as being
    // pulled in rather than a hard, only-checked-at-release hit-test.
    // Pure arithmetic against dragDeckRect/dragTapeRect (both cached at
    // pointerdown) -- no DOM read, so this can't reintroduce the
    // layout-thrash cost the fix above just removed. Only while a side
    // face is showing, matching the existing insert-eligibility rule
    // below (front/back are never insert targets, so pulling toward the
    // deck for them would be a lie).
    if (dragDeckRect && dragTapeRect && isSideFace(currentFace)) {
      var dist = distanceToRect(e.clientX, e.clientY, dragDeckRect);
      var near = dist <= SNAP_MARGIN_PX;
      if (near !== dragNearDeck) {
        dragNearDeck = near;
        tapeObject.classList.toggle('eva-radio-widget__tape-object--near-deck', near);
      }
      if (near) {
        var pull = 1 - (dist / SNAP_MARGIN_PX); // 0 at the zone's outer edge, 1 once the pointer is actually over the deck face
        var tapeCenterX = dragTapeRect.left + dragTapeRect.width / 2;
        var tapeCenterY = dragTapeRect.top + dragTapeRect.height / 2;
        var targetDx = dragDeckRect.cx - tapeCenterX; // the dx that would land the tape's center exactly on the deck's center
        var targetDy = dragDeckRect.cy - tapeCenterY;
        dx = dx + (targetDx - dx) * pull;
        dy = dy + (targetDy - dy) * pull;
      }
    }

    tapeObject.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
  }

  // Shared by a normal release and every "the pointer got lost" path
  // below (pointercancel, a stray window pointerup with a mismatched id,
  // losing focus mid-drag) -- always resets every piece of drag state and
  // springs the tape back to its rest position. Idempotent (checks
  // `dragging` itself), so it's safe to wire to broad, overlapping event
  // sources without worrying about double-firing.
  function forceEndDrag() {
    if (!dragging) { return; }
    dragging = false;
    activePointerId = null;
    dragDeckRect = null;
    dragTapeRect = null;
    dragNearDeck = false;
    tapeObject.classList.remove('eva-radio-widget__tape-object--dragging', 'eva-radio-widget__tape-object--near-deck');
    tapeObject.style.transition = '';
    if (dragMoved) {
      tapeObject.style.transform = ''; // spring back to the rack slot -- same CSS transition (restored above) every normal release already used
      dragMoved = false;
    }
  }

  function onPointerUp(e) {
    if (!dragging || e.pointerId !== activePointerId) { return; }
    activePointerId = null;
    try { tapeObject.releasePointerCapture(e.pointerId); } catch (err) {}
    tapeObject.style.transition = '';
    tapeObject.classList.remove('eva-radio-widget__tape-object--dragging', 'eva-radio-widget__tape-object--near-deck');

    if (!dragMoved) {
      dragging = false;
      dragDeckRect = null;
      dragTapeRect = null;
      // 2026-08-06: click/tap on the tape body ALWAYS just cycles faces,
      // at every breakpoint, idle or playing -- never starts playback.
      // (Previously a tap immediately after a neighbor-slot tap played the
      // tape instead -- pendingTapToPlay -- which read as "tapping another
      // tape plays it" and made it impossible to browse a second tape's
      // faces without committing to play it first. Playback is explicit
      // now: drag-to-insert, the PLAY button, or Enter.)
      advanceFace(); // click/tap, no real drag: cosmetic cycle only, never plays
      return;
    }

    // Phase 1b: the drop target is the whole deck face (deckFaceRect(),
    // generous on purpose) PLUS a further SNAP_MARGIN_PX magnetic buffer
    // around it, not the tiny calibrated door polygon directly --
    // distanceToRect() returns 0 for a point already inside the rect, so
    // one "<= SNAP_MARGIN_PX" check covers "released inside the deck
    // face" and "released within the magnetic zone around it" at once.
    // Reusing dragDeckRect (cached at pointerdown) rather than a fresh
    // deckFaceRect() call here too -- no reason to re-measure between
    // move and up.
    var deck = dragDeckRect;
    dragging = false;
    dragDeckRect = null;
    dragTapeRect = null;
    var hit = deck && distanceToRect(e.clientX, e.clientY, deck) <= SNAP_MARGIN_PX;
    if (hit && isSideFace(currentFace)) {
      insertFocusedTape(deck.cx, deck.cy);
    } else {
      tapeObject.style.transform = ''; // spring back -- CSS transition (restored above) animates the return -- also where a front/back drop lands, since front/back are never insert targets
      dragMoved = false;
    }
  }

  // --- Init --------------------------------------------------------------

  ready(function () {
    // Tape rack is a separate, non-critical subsystem -- a failure here
    // must never take down the radio's own transport (eva-radio.js inits
    // independently), so the whole body runs inside one try/catch.
    try {
      var radioAssets = window.EvaRadioAssets || {};
      if (radioAssets.tapeBrowse) {
        browseAudio = new Audio(radioAssets.tapeBrowse);
        browseAudio.preload = 'auto';
        // Found 2026-08-05 during a "true global mute" audit: this and
        // clunkAudio below were the only two Audio objects in the whole
        // radio/Källare system with no EvaMute wiring at all (every
        // sibling sound already had it) -- the rack's own browse-tick
        // and mechanical clunk played right through a global mute.
        if (window.EvaMute) { window.EvaMute.applyToElement(browseAudio); }
      }
      initCarouselTicks(radioAssets.carouselTicks);
      if (radioAssets.mechStop) {
        clunkAudio = new Audio(radioAssets.mechStop);
        clunkAudio.preload = 'auto';
        if (window.EvaMute) { window.EvaMute.applyToElement(clunkAudio); }
      }
      tapeRack = document.getElementById('EvaRadioTapeRack');
      slotPrev = document.getElementById('EvaRadioTapeSlotPrev');
      slotCenter = document.getElementById('EvaRadioTapeSlotCenter');
      slotNext = document.getElementById('EvaRadioTapeSlotNext');
      slotPrevImg = document.getElementById('EvaRadioTapeSlotPrevImg');
      slotNextImg = document.getElementById('EvaRadioTapeSlotNextImg');
      tapeObject = document.getElementById('EvaRadioTapeObject');
      tapeFlip = document.getElementById('EvaRadioTapeFlip');
      tapeImg = document.getElementById('EvaRadioTapeImg');
      sideSelect = document.getElementById('EvaRadioTapeSideSelect');
      sideSelectA = document.getElementById('EvaRadioTapeSideSelectA');
      sideSelectB = document.getElementById('EvaRadioTapeSideSelectB');
      playBtn = document.getElementById('EvaRadioTapePlayBtn');
      deckOverlayWrap = document.getElementById('EvaRadioDeckOverlay');
      deckOverlayImg = document.getElementById('EvaRadioDeckOverlayImg');
      hubLeft = document.getElementById('EvaRadioHubLeft');
      hubRight = document.getElementById('EvaRadioHubRight');
      doorPopEl = document.getElementById('EvaRadioDoorPop');
      stageEl = document.getElementById('EvaRadioStage');
      insertCorridorEl = document.getElementById('EvaRadioInsertCorridor');

      try {
        // Deck calibration (?deckcal=1) removed 2026-08-26 -- dev-only tool, stripped before launch.
      } catch (calErr) {
        console.error('[eva-radio-tape] deck calibration mode failed', calErr);
      }

      if (!tapeRack || !tapeObject || !tapeFlip || !tapeImg) { return; }

      // Rack-misalignment fix, take 2 (2026-08-06): the FIRST attempt --
      // calling syncRackAlignment() synchronously right after
      // lockTapeAspect() sets --eva-tape-aspect (see that function) --
      // turned out to still measure the WRONG width every time, because
      // .eva-radio-widget__tape-flip has `transition: aspect-ratio 0.3s
      // ease` (eva-radio.css): setting the custom property doesn't
      // resize the element instantly, it kicks off a 300ms animation
      // from the old aspect ratio (the CSS fallback, 800/505 landscape)
      // to the real one (e.g. memphis's 1206/1885 portrait) -- measuring
      // synchronously right after the property write catches slotCenter
      // still at (or barely past) its OLD, WRONG width. Confirmed via a
      // throttled (slow-3G) live repro: the synchronous call left the
      // misalignment completely unchanged, byte-for-byte, every time.
      // transitionend is the actual completion signal -- re-measuring
      // then catches the tape card's real, final width. (The synchronous
      // call in lockTapeAspect() is left in place too -- harmless, and
      // still correct for the case where the aspect ratio doesn't
      // actually change value, which fires no transition at all.)
      tapeFlip.addEventListener('transitionend', function (e) {
        if (e.propertyName === 'aspect-ratio') { syncRackAlignment(); }
      });

      // Take 3: even the transitionend-triggered call above still left a
      // small (~7-8px, live-measured) residual error. Cause: .eva-radio-
      // widget__tape-rack ALSO has `transition: transform 0.35s ease`
      // (it's what animates the rack sliding to a newly-computed
      // --eva-rack-align-x rather than snapping). Custom properties
      // themselves are NOT interpolated by a CSS transition (no @property
      // syntax declared here) -- getComputedStyle().getPropertyValue()
      // returns the new target value the instant it's set, but the
      // VISUAL transform driven by that var() is still mid-animation
      // toward it. syncRackAlignment()'s "idempotent" back-out math reads
      // BOTH of those together (currentOffset from the custom property,
      // slotRect from the live/painted position) -- calling it while a
      // rack-transform transition from an EARLIER correction is still
      // in-flight mixes an already-resolved target offset with a
      // not-yet-arrived rendered position, producing a slightly wrong
      // new offset. Listening for the rack's OWN transitionend and
      // re-running once more closes the loop: this call always lands
      // AFTER the transform has genuinely settled, so it measures
      // consistent data and (per the function's own "never compounds a
      // stale correction" design) converges to the exact right offset,
      // which changes nothing further and starts no new transition --
      // self-terminating, not a risk of looping forever.
      tapeRack.addEventListener('transitionend', function (e) {
        if (e.propertyName === 'transform') { syncRackAlignment(); }
      });

      tapeObject.addEventListener('pointerdown', onPointerDown);
      tapeObject.addEventListener('pointermove', onPointerMove);
      tapeObject.addEventListener('pointerup', onPointerUp);
      tapeObject.addEventListener('pointercancel', onPointerUp);

      // Phase 1b safety net (2026-08-04) -- "never leave a tape stranded".
      // setPointerCapture (onPointerDown) is supposed to guarantee move/up/
      // cancel keep firing on tapeObject even once the pointer leaves its
      // visual bounds, but that guarantee depends on capture actually
      // having succeeded (silently swallowed by the try/catch if not) and
      // on the browser/OS reliably delivering a matching-id cancel in
      // every interruption case (an incoming call, an OS-level gesture
      // stealing the touch, etc. don't all behave identically across
      // engines). Window-level listeners are the backstop for exactly the
      // cases the element-level ones can't be relied on for: a pointerup
      // that lands somewhere pointer capture didn't actually route back
      // to tapeObject, and anything that ends the gesture without ANY
      // pointer event at all (losing window focus, tab switch).
      window.addEventListener('pointerup', function (e) {
        if (!dragging) { return; }
        if (e.pointerId === activePointerId) {
          onPointerUp(e); // correct pointer, full insert-check logic
        } else {
          forceEndDrag(); // some other pointer ended while we're mid-drag -- safest to abandon rather than risk this event's coordinates belonging to the wrong gesture
        }
      });
      window.addEventListener('pointercancel', function () { forceEndDrag(); });
      window.addEventListener('blur', forceEndDrag);
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) { forceEndDrag(); }
      });

      tapeRack.addEventListener('wheel', onRackWheel, { passive: false });
      tapeRack.addEventListener('touchstart', onRackTouchStart, { passive: true });
      tapeRack.addEventListener('touchmove', onRackTouchMove, { passive: true });
      tapeRack.addEventListener('touchend', onRackTouchEnd, { passive: true });
      tapeRack.addEventListener('keydown', onRackKeydown);

      // Tap-to-centre (Phase 2): plain click, not pointer-capture drag --
      // these slots never drag, only tap/click/Enter. renderNeighborSlot()
      // is what decides whether there's actually a real tape to center on
      // (onNeighborSlotTap no-ops otherwise), so binding unconditionally
      // here is safe even before any tape data has loaded.
      if (slotPrev) {
        slotPrev.addEventListener('click', function () { onNeighborSlotTap(-1); });
        slotPrev.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNeighborSlotTap(-1); }
        });
      }
      if (slotNext) {
        slotNext.addEventListener('click', function () { onNeighborSlotTap(1); });
        slotNext.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onNeighborSlotTap(1); }
        });
      }

      if (sideSelectA) { sideSelectA.addEventListener('click', function () { onSideSelectClick('a'); }); }
      if (sideSelectB) { sideSelectB.addEventListener('click', function () { onSideSelectClick('b'); }); }
      // Explicit PLAY control -- a real sibling <button> of tapeObject (not
      // a child), so it's outside tapeObject's own pointerdown/drag
      // handling entirely; no stopPropagation/guard needed the way a child
      // element would require.
      if (playBtn) { playBtn.addEventListener('click', playCenteredTape); }

      // "Tapes shown on open" -- dispatched by eva-radio.js's
      // restoreWidget(), see that file's own comment. (The "hidden while
      // playing" half lives in the existing eva:radio:playstate listener
      // below, folded in rather than adding a second listener for the
      // same event.)
      // Widget was minimized (undetectable/zero-rect door) while these
      // fired -- syncRackAlignment() needs a fresh measurement now that
      // it's visible again.
      document.addEventListener('eva:radio:opened', function () { showRack(); syncRackAlignment(); });
      // Pressing the cassette door (eva-radio.js's requestShowRack()) is a
      // real open/close toggle on the rack (2026-08-09, see
      // toggleRackFromDoor()'s own comment) -- Per: no popup list,
      // nothing else, just open or closed.
      document.addEventListener('eva:radio:door:pressed', toggleRackFromDoor);
      // Widget resize-handle drag start/end (eva-radio.js) -- see
      // onRadioResizeGesture's own comment for why this needs its own
      // rAF-driven tracking rather than relying on the ResizeObserver
      // below alone.
      document.addEventListener('eva:radio:resize', onRadioResizeGesture);

      radioImg = document.querySelector('.eva-radio-widget__img');
      if (radioImg && 'ResizeObserver' in window) {
        // Fires on widget resize AND on an angle swap to a photo with a
        // different aspect ratio (img.src changing its rendered height at
        // the same width) -- one observer covers both triggers without
        // needing to listen for either event specifically. Also the
        // trigger for re-aligning the rack to the deck's door -- a resize
        // (drag-handle or window) can change the ratio between the
        // widget's own centre and the door's real position just as much
        // as an angle swap can.
        //
        // Misalignment-on-first-load bug (2026-08-06): a slow/uncached
        // load of the boombox photo left the rack visibly off-center from
        // the door until a page reload -- a cached reload lays out
        // instantly (img.complete true before any of this code runs), a
        // fresh load doesn't. Root cause: .eva-radio-widget__stage's
        // aspect-ratio starts on a hardcoded CSS fallback (1901/1057, see
        // eva-radio.css) until eva-radio.js's syncStageAspect() reads the
        // real img.naturalWidth/Height off the img's own 'load' event and
        // overwrites --eva-radio-stage-aspect. onTapesReady() below calls
        // syncRackAlignment() as soon as the (small, fast) tape manifest
        // fetch resolves, which on a slow connection can easily land
        // before the (large photo) image's load event -- doorRect()
        // still returns a non-zero rect at that point (the fallback
        // aspect ratio lays out FINE, just at the WRONG ratio), so the
        // `!door.width` guard doesn't catch it and a wrong offset gets
        // locked in. Observing radioImg alone should in theory already
        // catch the later resize (the stage's height changes when the
        // aspect ratio updates, and radioImg is height:100% of the
        // stage) -- also observing stageEl directly is the more robust
        // fix Per asked for: it reacts to the aspect-ratio custom
        // property change itself, the actual thing that moves the door,
        // rather than depending on that change happening to also resize
        // the img element. The explicit 'load' listener below is the
        // third, most direct belt-and-suspenders layer -- straight off
        // the same event syncStageAspect() itself listens for, no
        // ResizeObserver indirection at all.
        rackResizeObserver = new ResizeObserver(function () { syncRackSizing(); syncRackAlignment(); });
        rackResizeObserver.observe(radioImg);
        if (stageEl) { rackResizeObserver.observe(stageEl); }
        if (!radioImg.complete) {
          radioImg.addEventListener('load', function () { syncRackSizing(); syncRackAlignment(); }, { once: true });
        }
      }

      rackUpBtn = document.getElementById('EvaRadioTapeRackUp');
      rackDownBtn = document.getElementById('EvaRadioTapeRackDown');
      if (rackUpBtn) { rackUpBtn.addEventListener('click', function () { setFocusedTapeIndex(focusedTapeIndex - 1); }); }
      if (rackDownBtn) { rackDownBtn.addEventListener('click', function () { setFocusedTapeIndex(focusedTapeIndex + 1); }); }
      updateArrowDisabled();

      // Tape's own hover/touch tooltip, via the same chip eva-radio.js
      // uses for every other control (data-eva-tooltip is toggled on/off
      // by updateDragTooltip() as the deck's contents change -- see there).
      // pointerover/out filtered to real mice, same reasoning as
      // eva-radio.js's delegated tooltip listeners -- touch's own
      // synthetic mouse events would otherwise hide the touch-triggered
      // chip almost immediately instead of leaving it up for 1.5s.
      if (window.EvaRadioWidget) {
        tapeObject.addEventListener('pointerover', function (e) {
          if (e.pointerType !== 'mouse') { return; }
          var text = tapeObject.getAttribute('data-eva-tooltip');
          if (text) { window.EvaRadioWidget.showTooltip(tapeObject, text); }
        });
        tapeObject.addEventListener('pointerout', function (e) {
          if (e.pointerType !== 'mouse') { return; }
          if (!tapeObject.contains(e.relatedTarget)) { window.EvaRadioWidget.hideTooltip(); }
        });
        var tapeTooltipTouched = false;
        tapeObject.addEventListener('touchstart', function () {
          if (tapeTooltipTouched) { return; }
          tapeTooltipTouched = true;
          var text = tapeObject.getAttribute('data-eva-tooltip');
          if (!text) { return; }
          window.EvaRadioWidget.showTooltip(tapeObject, text);
          setTimeout(window.EvaRadioWidget.hideTooltip, 1500);
        }, { passive: true });
      }

      document.addEventListener('eva:radio:angle', function (e) {
        currentAngleInteractive = !!(e.detail && e.detail.interactive);
        currentAngle = (e.detail && e.detail.angle) || 'front';
        applyDeckOverlay(); // the overlay/hubs only ever show on the 'front' angle -- see applyDeckOverlay()
        updateDeckWindowEffects();
        // Front/above have different door hotspot polygons (and different
        // photo aspect ratios), so the deck's real x-centre moves with the
        // angle -- realign now. If the new photo hasn't finished loading
        // yet this reads a momentarily stale size, same as syncRackSizing()
        // already can -- the ResizeObserver below re-fires and corrects
        // both once the image actually resizes post-load.
        syncRackAlignment();
      });
      // Deck state changes through a path other than a drag-insert here
      // (the natural end-of-side flip's accessible button fallback,
      // flipToSide() in eva-radio.js) -- keep deckTapeId/deckSide and the
      // overlay/hubs/tooltip in sync, but never touch the rack's own
      // face: Per's spec has the rack sitting on the front cover from the
      // moment of insert onward, straight through natural side-flips too.
      document.addEventListener('eva:radio:tape:side', function (e) {
        if (!e.detail) { return; }
        deckTapeId = e.detail.tapeId;
        deckSide = e.detail.sideId;
        applyDeckOverlay();
        updateDeckWindowEffects();
        updateDragTooltip();
      });
      // STOP and PAUSE only ever affect audio + the deck-window overlay/
      // hubs -- never the rack card (Per: "no eject animation to the
      // rack, no state change on the covers"). reason distinguishes the
      // two: pause leaves deckStopped alone (overlay stays put, frozen,
      // hubs just stop spinning via deckPlaying); stop sets deckStopped
      // so applyDeckOverlay() fades the art out. The door-pop (a
      // deck-window flash, not a rack effect) still fires on stop,
      // independent of what's focused in the rack right now.
      // FF/REW hold state from eva-radio.js's beginSeekHold()/endSeekHold()
      // -- drives the hubs' spin direction/speed independent of deckPlaying
      // (see updateDeckWindowEffects()'s mode priority).
      document.addEventListener('eva:radio:seekstate', function (e) {
        currentSeekAction = (e.detail && e.detail.action) || null;
        updateDeckWindowEffects();
      });
      document.addEventListener('eva:radio:playstate', function (e) {
        var playing = !!(e.detail && e.detail.playing);
        var reason = e.detail && e.detail.reason;
        deckPlaying = playing;
        if (playing) {
          deckStopped = false;
          hideRackForPlayback(); // Phase 2 visibility model -- unconditional: hides even if the user had already pulled the rack back out via the door toggle
        } else if (reason === 'stop') {
          deckStopped = true;
        }
        applyDeckOverlay();
        updateDeckWindowEffects();
        if (reason === 'stop') { playDoorPop(); }
      });
      // Tape data is fetched once by eva-radio.js (assets/eva-tapes.json)
      // and handed off here rather than duplicated -- handle both orderings
      // between the two scripts' own ready() callbacks: the fetch may have
      // already resolved (checked synchronously first) or may still be
      // in flight (the event covers that case).
      function onTapesReady(tapesList) {
        tape = tapesList && tapesList[focusedTapeIndex];
        if (!tape) { return; }
        currentFace = 'front';
        renderFace('front');
        lockTapeAspect(); // first render bypasses showFace() entirely -- without this the card sits on the static landscape fallback (see CSS --eva-tape-aspect default) until the first cycle/focus/insert ever touches showFace()
        updateDragTooltip();
        updateSideSelectUI(); // same reason -- this first render is the other bypass of showFace() (see its own comment above), so the side-select buttons need their own explicit call here too or they stay hidden until the first face change
        updatePlayBtnUI(); // same bypass, same fix -- the PLAY button
        preloadImages();
        renderNeighborPreviews();
        tapeRack.hidden = false;
        syncRackSizing(); // rack was hidden until now, so this is the first time its slots have a real size to compute
        syncRackAlignment(); // same reasoning -- first time the centre slot has a real rect to measure against the door
        updateArrowDisabled(); // tapeCount() couldn't see real data until now -- re-check now that it can
        try {
          initRackDevPrototype(); // no-ops without ?tapedev=1; needs `tape`, so waits behind the same manifest fetch
        } catch (rackErr) {
          console.error('[eva-radio-tape] dev rack prototype failed', rackErr);
        }
      }
      if (window.EvaRadioWidget && typeof window.EvaRadioWidget.getTapes === 'function' && window.EvaRadioWidget.getTapes().length) {
        onTapesReady(window.EvaRadioWidget.getTapes());
      } else {
        document.addEventListener('eva:radio:tapes:ready', function (e) {
          onTapesReady((e.detail && e.detail.tapes) || []);
        });
      }
    } catch (tapeErr) {
      console.error('[eva-radio-tape] init failed', tapeErr);
    }
  });

  // --- Deck calibration mode (?deckcal=1) -- REMOVED 2026-08-26 ----------
  // Dev-only drag-to-calibrate overlay, stripped before launch at Per's
  // request so no query param can surface it on the live storefront. The
  // values it existed to produce are already baked into the CSS vars it
  // used to nudge; recover the tool from git history if it is ever needed
  // again (last present at 8121939).

  // --- Dev-only rack-feel prototype (?tapedev=1) --------------------------
  // NOT shipped. Duplicates the one real tape into 5 placeholder entries
  // to evaluate a fuller vertical-scroll-rack feel beyond the 3-fixed-slot
  // shipped version. Renders as a temporary overlay built entirely at
  // runtime -- no production markup, no effect on the default experience,
  // invisible without the query param.

  function initRackDevPrototype() {
    var params = new URLSearchParams(window.location.search);
    if (params.get('tapedev') !== '1') { return; }
    if (!tape) { return; }

    var labels = ['Memphis Rarities', 'Basement Loosies', 'Porch Tape #3', 'Late Night Dubs', 'Found On A Bench'];
    var items = labels.map(function (label, i) {
      return { id: 'dev-' + i, label: label, image: (tape.images && tape.images.front) || '' };
    });

    var overlay = document.createElement('div');
    overlay.id = 'EvaRadioTapeDevRack';
    overlay.setAttribute('aria-hidden', 'true'); // prototype, not part of the accessible experience
    overlay.style.cssText = [
      'position:fixed', 'top:0', 'right:0', 'bottom:0', 'width:180px',
      'overflow-y:auto', 'z-index:9999', 'background:rgba(0,0,0,0.85)',
      'padding:40vh 0', 'scroll-snap-type:y proximity', 'display:flex',
      'flex-direction:column', 'align-items:center', 'gap:24px'
    ].join(';');

    items.forEach(function (item) {
      var card = document.createElement('div');
      card.className = 'eva-tape-dev-card';
      card.style.cssText = 'scroll-snap-align:center;flex:none;width:120px;transition:transform 0.15s ease, opacity 0.15s ease;text-align:center;';
      var img = document.createElement('img');
      img.src = item.image;
      img.style.cssText = 'width:100%;display:block;pointer-events:none;';
      var caption = document.createElement('div');
      caption.textContent = item.label;
      caption.style.cssText = 'color:#fff;font-size:11px;margin-top:4px;font-family:monospace;';
      card.appendChild(img);
      card.appendChild(caption);
      overlay.appendChild(card);
    });

    document.body.appendChild(overlay);

    function updateScale() {
      var mid = overlay.getBoundingClientRect().top + overlay.clientHeight / 2;
      var cards = overlay.querySelectorAll('.eva-tape-dev-card');
      cards.forEach(function (card) {
        var r = card.getBoundingClientRect();
        var dist = Math.abs((r.top + r.height / 2) - mid);
        var scale = Math.max(0.55, 1 - dist / 500);
        card.style.transform = 'scale(' + scale + ')';
        card.style.opacity = String(Math.max(0.35, 1 - dist / 400));
      });
    }
    overlay.addEventListener('scroll', updateScale);
    updateScale();

    console.info('[eva-radio-tape] dev rack prototype active (?tapedev=1) -- scroll the right-edge overlay to evaluate the v2 rack feel. Not shipped.');
  }
})();
